/**
 * The refusals a client can meet when their money will not move.
 *
 * Each of these used to arrive as the server's own sentence inside a generic red
 * box. These are the worst moments to be terse in: the client is being told no,
 * about their own money, and a paragraph that trails off leaves them with no
 * idea whether to wait, to pick something else, or to call someone.
 *
 * So each one becomes three short parts — what happened, what it means, and the
 * one thing to do next. Never a code, never a rule name, never anything about
 * how the platform is built: a person reads that we cannot send money to a new
 * account yet, not that a cooling-off window is open.
 *
 * `rail_not_accepted` is deliberately absent. It is refused to STAFF attributing
 * a payment by hand; a client never receives it. What a client is told about
 * payment methods lives on the funding screen, before they send anything.
 *
 * The wording here is provisional and will be reviewed by counsel; the structure
 * is not, and does not depend on that review.
 */
import { getApiErrorCode, getApiErrorMessage } from './apiError';
import axios from 'axios';
import { readerLocale } from '../i18n/language';

export interface MoneyRefusal {
  code: string;
  /** What happened, as the client experienced it. */
  title: string;
  /** What it means for them. */
  detail: string;
  /** The single next step. Empty when there is genuinely nothing to do but wait. */
  next: string;
  /** Present when the refusal ends by itself on a known date. */
  clearsOn: string | null;
}

/** A backend `detail` object, if the error carries one. */
function detailOf(err: unknown): Record<string, unknown> | null {
  if (!axios.isAxiosError(err)) return null;
  const detail: unknown = err.response?.data?.detail;
  return detail && typeof detail === 'object' && !Array.isArray(detail)
    ? (detail as Record<string, unknown>)
    : null;
}

function str(detail: Record<string, unknown> | null, key: string): string | null {
  const value = detail?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * An ISO date from the server as a client would write it. Returns null rather
 * than inventing a date: a sentence with "undefined" in it is worse than one
 * that simply does not mention when.
 */
export function formatClearDate(iso: string | null): string | null {
  if (iso === null) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString(readerLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

function countryRefusal(detail: Record<string, unknown> | null): MoneyRefusal {
  const name = str(detail, 'country_name');
  const where = name ?? 'that country';
  return {
    code: 'country_not_supported',
    title: `We cannot work with ${where}`,
    detail:
      `Payments to and from ${where} cannot be processed by the banks we send money `
      + 'through, so we cannot hold an account or move money connected to it. This is '
      + 'not a decision about you.',
    next: 'If you have another country of residence or another bank account, use that one.',
    clearsOn: null,
  };
}

function notFundingSource(detail: Record<string, unknown> | null): MoneyRefusal {
  const until = formatClearDate(str(detail, 'window_ends_on'));
  return {
    code: 'destination_not_funding_source',
    title: 'We can only send money back the way it came',
    detail:
      'Your money goes back to a bank account you have sent money from. '
      + (until ? `This applies until ${until}.` : ''),
    next: 'Choose an account you have sent money from. If that account is closed, contact us.',
    clearsOn: until,
  };
}

// 0097: the 180-day removal lock (`bank_account_locked`) was replaced by the
// in-flight guard below. The old case is gone with its API code.
function accountInUse(): MoneyRefusal {
  return {
    code: 'bank_account_in_use',
    title: 'This account has a withdrawal in progress',
    detail: 'A transfer to this account has not completed yet, so it stays on your profile until it does.',
    next: 'You can remove it once the transfer completes. If something looks wrong, contact us.',
    clearsOn: null,
  };
}

const SECURITY_HOLD: MoneyRefusal = {
  code: 'security_hold',
  title: 'Your account is on hold',
  // The client raised this hold themselves, so there is nothing to keep back —
  // but the reassurance about their balance belongs first, not last.
  detail:
    'A change to your account was reported as unauthorised, so withdrawals and account '
    + 'changes are paused. Your balance and your holdings are not affected.',
  next: 'We will contact you to confirm your identity. If you have not heard from us, contact us.',
  clearsOn: null,
};

function coolingOff(detail: Record<string, unknown> | null): MoneyRefusal {
  const from = formatClearDate(str(detail, 'clears_on'));
  return {
    code: 'destination_cooling_off',
    title: 'This account is too new to receive money',
    detail: from
      ? `You can send money to it from ${from}. A short wait on a newly added account `
        + 'is what stops someone else redirecting your money.'
      : 'A short wait on a newly added account is what stops someone else redirecting '
        + 'your money.',
    next: 'Choose an account you have used before, or come back on that date.',
    clearsOn: from,
  };
}

function phoneChangeHold(detail: Record<string, unknown> | null): MoneyRefusal {
  const from = formatClearDate(str(detail, 'clears_on'));
  return {
    code: 'phone_change_hold',
    title: 'Your phone number changed recently',
    // Why the wait exists, in the client's terms, and no more: explaining how
    // long a takeover has to be noticed is a briefing for whoever took the
    // account over.
    detail: from
      ? `Money can leave your account again from ${from}. Your balance and your `
        + 'holdings are not affected.'
      : 'Money cannot leave your account for a few days. Your balance and your '
        + 'holdings are not affected.',
    next: 'If you did not change your number, contact us now.',
    clearsOn: from,
  };
}

/**
 * Both waits at once, which is ONE event and not two.
 *
 * A client who changes their phone number and adds a bank account in the same
 * week — somebody moving house, or leaving a bank and keeping their number —
 * trips both controls off one decision. Told separately they arrive days apart:
 * a wait on the number now, and a call they never heard about waiting on the
 * other side of it. Each message is true and the pair reads as two things going
 * wrong rather than one thing in progress.
 */
function bothWaits(detail: Record<string, unknown> | null): MoneyRefusal {
  const from = formatClearDate(str(detail, 'clears_on'));
  return {
    code: 'phone_change_and_new_account',
    title: 'We need to confirm two changes with you',
    detail:
      'Your phone number changed recently and the account you added is waiting for us '
      + 'to confirm it with you. '
      + (from
        ? `We will call you on the number we already have on file, from ${from}. `
        : 'We will call you on the number we already have on file. ')
      + 'Your balance and your holdings are not affected.',
    next: 'Keep your phone to hand. If you did not make these changes, contact us now.',
    clearsOn: from,
  };
}

/** 0113 (#157): a fund position still inside its commitment window, with no
 *  early-withdrawal charge configured to accept, cannot be withdrawn yet. */
function notYetEligible(detail: Record<string, unknown> | null): MoneyRefusal {
  const from = formatClearDate(str(detail, 'eligible_at'));
  return {
    code: 'withdrawal_not_yet_eligible',
    title: 'This money is still committed',
    detail: from
      ? `The money in this series is committed until ${from}, as set out in its terms. `
        + 'Your balance and your holdings are not affected.'
      : 'The money in this series is committed for its agreed term. '
        + 'Your balance and your holdings are not affected.',
    next: from
      ? `You can request this withdrawal from ${from}.`
      : 'You can request this withdrawal once the term ends.',
    clearsOn: from,
  };
}

/**
 * The designed state for a refusal, or null when this is not one of them — in
 * which case the caller shows its ordinary error, unchanged.
 */
export function describeMoneyRefusal(err: unknown): MoneyRefusal | null {
  const code = getApiErrorCode(err);
  if (code === null) return null;
  const detail = detailOf(err);
  switch (code) {
    case 'country_not_supported':
      return countryRefusal(detail);
    case 'destination_not_funding_source':
      return notFundingSource(detail);
    case 'bank_account_in_use':
      return accountInUse();
    case 'security_hold':
      return SECURITY_HOLD;
    case 'destination_cooling_off':
      return coolingOff(detail);
    case 'phone_change_hold':
      return phoneChangeHold(detail);
    case 'phone_change_and_new_account':
      return bothWaits(detail);
    case 'withdrawal_not_yet_eligible':
      return notYetEligible(detail);
    default:
      return null;
  }
}

/**
 * One call for a catch block: either a designed refusal, or an ordinary message.
 * Never both — showing a red box under a designed state tells the client twice,
 * and the second telling is the one written for a machine.
 */
export function splitError(
  err: unknown,
  fallback: string,
): { refusal: MoneyRefusal | null; message: string | null } {
  const refusal = describeMoneyRefusal(err);
  return refusal !== null
    ? { refusal, message: null }
    : { refusal: null, message: getApiErrorMessage(err, fallback) };
}
